import { z } from "zod";

/**
 * MHD-Warnungen. Schwellen in Tagen Restlaufzeit:
 * - Mandanten-Standard (z. B. Warnung 90 / kritisch 30)
 * - je Artikel überschreibbar (z. B. Frischware 14 / 5)
 */
export const bestBeforeSettingsSchema = z
  .object({
    warningDays: z.number().int().min(1).max(3650),
    criticalDays: z.number().int().min(0).max(3650),
  })
  .refine((s) => s.criticalDays < s.warningDays, { message: "Kritisch muss kleiner als Warnung sein", path: ["criticalDays"] });

export type BestBeforeSettings = z.infer<typeof bestBeforeSettingsSchema>;
export const DEFAULT_BEST_BEFORE: BestBeforeSettings = { warningDays: 90, criticalDays: 30 };

export type BestBeforeLevel = "expired" | "critical" | "warning" | "ok";

export interface BestBeforeOverride {
  warningDays?: number | null;
  criticalDays?: number | null;
}

/** Effektive Schwellen: Artikel-Override vor Mandanten-Standard */
export function effectiveBestBefore(tenant: BestBeforeSettings, product?: BestBeforeOverride): BestBeforeSettings {
  return {
    warningDays: product?.warningDays ?? tenant.warningDays,
    criticalDays: product?.criticalDays ?? tenant.criticalDays,
  };
}

/** Ganze Kalendertage bis zum MHD (MHD-Tag selbst zählt noch als haltbar: 0 = läuft heute ab) */
export function daysUntil(bestBefore: string, now: Date): number {
  const [y, m, d] = bestBefore.slice(0, 10).split("-").map(Number) as [number, number, number];
  const target = Date.UTC(y, m - 1, d);
  const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  return Math.round((target - today) / 86_400_000);
}

export function bestBeforeLevel(daysLeft: number, s: BestBeforeSettings): BestBeforeLevel {
  if (daysLeft < 0) return "expired";
  if (daysLeft <= s.criticalDays) return "critical";
  if (daysLeft <= s.warningDays) return "warning";
  return "ok";
}

/** Menschlich lesbar: "abgelaufen seit 3 Tagen", "läuft heute ab", "läuft ab in 12 Tagen" */
export function describeDaysLeft(daysLeft: number): string {
  if (daysLeft < -1) return `abgelaufen seit ${-daysLeft} Tagen`;
  if (daysLeft === -1) return "seit gestern abgelaufen";
  if (daysLeft === 0) return "läuft heute ab";
  if (daysLeft === 1) return "läuft morgen ab";
  if (daysLeft < 60) return `läuft ab in ${daysLeft} Tagen`;
  const months = Math.round(daysLeft / 30.4);
  return `läuft ab in ${months} Monaten`;
}

/** Bestandsmeldung an die Shops je Kunde */
export const stockSyncSettingsSchema = z.object({
  /** angemeldete, noch nicht eingebuchte Ware (Zulauf) als verkaufbar melden */
  includeAnnounced: z.boolean().default(false),
  /** Stück je Artikel, die nie an den Shop gemeldet werden (Schutz vor Überverkauf); je Artikel überschreibbar */
  bufferUnits: z.number().int().min(0).max(100_000).default(0),
});
export type StockSyncSettings = z.infer<typeof stockSyncSettingsSchema>;

/** Abweichung je Kanal (null = wie Kunden-Standard); der Puffer am einzelnen Artikel hat Vorrang */
export const channelStockSyncSchema = z.object({
  includeAnnounced: z.boolean().nullable().default(null),
  bufferUnits: z.number().int().min(0).max(100_000).nullable().default(null),
});
export type ChannelStockSync = z.infer<typeof channelStockSyncSchema>;
export const channelStockSyncOf = (config: unknown): ChannelStockSync => channelStockSyncSchema.parse((config as { stockSync?: unknown } | null)?.stockSync ?? {});
export const hasChannelStockOverride = (o: ChannelStockSync) => o.includeAnnounced != null || o.bufferUnits != null;
/** Bestand an den Kanal melden? (Standard ja; aus = Kunde pflegt den Bestand im Shop selbst) */
export const channelPushesStock = (config: unknown) => (config as { pushStock?: boolean } | null)?.pushStock !== false;

/**
 * Aufträge mit Fehlbestand. Ebenen: Auftrag (Knopf „trotz Fehlbestand übertragen“) > Shop > Kunde > Standard.
 * - mode "hold": bleibt im Portal mit Grund, geht automatisch raus, sobald Bestand da ist (Standard)
 * - mode "transfer": wird trotzdem übertragen; handover bestimmt, ob direkt an Logiful oder nur ins FFN (Vorbereitung)
 */
/**
 * Grenzen der Teillieferung (null/fehlend = keine Grenze – so verhalten sich ältere Einstellungen unverändert):
 * - minSharePercent: geteilt wird erst, wenn mindestens so viel % jetzt lieferbar ist (Warenwert; ohne Preise Stückzahl)
 * - maxParts: höchstens so viele Teile je Shop-Auftrag (2 = eine Restlieferung, die dann komplett abgewartet wird)
 */
export const partialLimitsSchema = z.object({
  minSharePercent: z.number().int().min(1).max(99).nullable().optional(),
  maxParts: z.number().int().min(2).max(20).nullable().optional(),
});
export type PartialLimits = z.infer<typeof partialLimitsSchema>;
/** Vorschlag für neu gewählte Teillieferung */
export const PARTIAL_LIMIT_DEFAULTS = { minSharePercent: 50, maxParts: 2 } as const;

export const backorderSettingsSchema = z.object({
  /** partial = Verfügbares sofort liefern, Rest als verknüpfte Restlieferung im Portal zurückhalten */
  mode: z.enum(["hold", "transfer", "partial"]).default("hold"),
  /** fulfiller = direkt an Logiful (FFN „Pending“), merchant = nur ins FFN (FFN „Preparation“, Logiful sieht ihn noch nicht) */
  handover: z.enum(["fulfiller", "merchant"]).default("fulfiller"),
  ...partialLimitsSchema.shape,
});
export type BackorderSettings = z.infer<typeof backorderSettingsSchema>;
/** Shop-Ebene: nur gesetzte Werte überschreiben den Kunden (bewusst ohne Standardwerte) */
export const backorderOverrideSchema = z.object({ mode: z.enum(["hold", "transfer", "partial"]).optional(), handover: z.enum(["fulfiller", "merchant"]).optional(), ...partialLimitsSchema.shape });

/** lesbare Grenzen, z. B. „ab 50 % lieferbar, höchstens 2 Teile“ */
export const partialLimitsText = (l: PartialLimits) =>
  [l.minSharePercent ? `ab ${l.minSharePercent} % lieferbar` : null, l.maxParts ? `höchstens ${l.maxParts} Teile` : null].filter(Boolean).join(", ") || "ohne Grenze";

/** lesbare Beschreibung für Verlauf und Anzeige */
export const backorderText = (s: Partial<BackorderSettings>) =>
  s.mode === "partial" ? `Fehlbestand: teilliefern (${partialLimitsText(s)}) – Verfügbares sofort, Rest als Restlieferung, sobald Bestand da ist` : s.mode === "transfer" ? `Fehlbestand: trotzdem übertragen – ${s.handover === "merchant" ? "nur ins Lager-System (FFN Vorbereitung), an Logiful sobald Bestand da ist" : "direkt an Logiful"}` : s.mode === "hold" ? "Fehlbestand: im Portal zurückhalten, bis Bestand da ist" : "wie Kunden-Einstellung";

export function resolveBackorderSettings(tenant: unknown, channel: unknown): BackorderSettings {
  const t = backorderSettingsSchema.parse(tenant ?? {});
  const c = backorderOverrideSchema.parse(channel ?? {});
  // Grenzen gehören zur gewählten Art: legt der Shop die Art fest, gelten seine Grenzen
  const limits = c.mode ? { minSharePercent: c.minSharePercent ?? null, maxParts: c.maxParts ?? null } : { minSharePercent: t.minSharePercent ?? null, maxParts: t.maxParts ?? null };
  return { mode: c.mode ?? t.mode, handover: c.handover ?? t.handover, ...limits };
}
